import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Phone, Bell, Loader2, ShieldCheck } from "lucide-react";

export default function PhonePromptModal({ force = false, onClose }) {
  const { user, setUser } = useAuth();
  const [share, setShare] = useState(true);
  const [phone, setPhone] = useState(user?.phone || "");
  const [busy, setBusy] = useState(false);
  const show = !!user && user.role !== "admin" && (force || (!user.phone && !user.phone_prompted));

  useEffect(() => {
    if (!show) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [show]);
  if (!show) return null;

  const submit = async () => {
    if (share && phone.replace(/\D/g, "").length < 10) return toast.error("Enter a phone number with at least 10 digits");
    setBusy(true);
    try {
      const { data } = await api.post("/profile/phone-prompt", { share, phone: share ? phone : null });
      setUser({ ...user, phone: data.phone, phone_prompted: true });
      toast.success(share ? "Thanks — you're set for tournament & training updates" : "No problem — you can add a number anytime in your profile");
      onClose?.(share);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return createPortal(
    <div className="fixed inset-0 z-[130] bg-black/85 backdrop-blur-sm flex items-center justify-center p-4" data-testid="phone-prompt-backdrop">
      <div className="bg-pb-surface border border-pb-red w-full max-w-md animate-fade-up shadow-[0_0_40px_rgba(163,230,53,0.15)]" data-testid="phone-prompt-modal">
        <div className="p-6 border-b border-pb-border">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-pb-red font-bold"><Phone className="w-3.5 h-3.5" /> One quick thing{force ? "" : " before you jump in"}</div>
          <h3 className="font-display font-black text-xl uppercase mt-2 leading-tight">Can we reach you by phone?</h3>
          <p className="text-sm text-zinc-400 mt-2">
            Tournament directors use it to text you <span className="text-white">court assignments, start times and schedule changes</span> on event day,
            and coaches use it for <span className="text-white">training session reminders</span>. It's never shown publicly or sold.
          </p>
        </div>
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-2" data-testid="phone-prompt-toggle">
            <button type="button" data-testid="phone-prompt-yes" onClick={() => setShare(true)}
              className={`py-3 border text-[11px] font-black uppercase tracking-widest transition-colors ${share ? "border-pb-red bg-pb-red text-black" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
              Yes, text me
            </button>
            <button type="button" data-testid="phone-prompt-no" onClick={() => setShare(false)}
              className={`py-3 border text-[11px] font-black uppercase tracking-widest transition-colors ${!share ? "border-zinc-400 bg-zinc-800 text-white" : "border-pb-border text-zinc-400 hover:border-zinc-500"}`}>
              Not now
            </button>
          </div>
          {share ? (
            <div>
              <label className="block text-[9px] uppercase tracking-[0.2em] text-zinc-500 font-bold mb-1.5">Mobile number *</label>
              <input data-testid="phone-prompt-input" type="tel" autoFocus value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567"
                onKeyDown={(e) => e.key === "Enter" && submit()}
                className="w-full bg-pb-elevated border border-pb-red/60 px-3 py-3 text-base text-white focus:outline-none focus:border-pb-red" />
              <div className="flex items-center gap-1.5 text-[10px] text-zinc-500 mt-1.5"><ShieldCheck className="w-3 h-3" /> Used only for event and training contact. Standard message rates apply.</div>
            </div>
          ) : (
            <div className="border border-pb-border bg-pb-elevated/40 p-3 text-xs text-zinc-400 flex items-start gap-2" data-testid="phone-prompt-skip-note">
              <Bell className="w-4 h-4 text-amber-300 shrink-0 mt-0.5" />
              <span>You'll still get in-app alerts, but you may miss same-day court calls. We'll ask once more when you register for a tournament.</span>
            </div>
          )}
          <button data-testid="phone-prompt-save" onClick={submit} disabled={busy || (share && !phone.trim())}
            className="w-full bg-pb-red text-black font-black uppercase tracking-widest text-xs py-3.5 hover:bg-[#BEF264] active:scale-[0.98] transition-all disabled:opacity-50 flex items-center justify-center gap-2">
            {busy && <Loader2 className="w-4 h-4 animate-spin" />}{share ? "Save & continue" : "Continue without a number"}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
